import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { linkFormSchema, type LinkFormData } from '@/schemas/linkSchemas';
import type { Link } from '@/types/legacy';
import { Loader2 } from 'lucide-react';

interface LinkFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  link?: Link | null;
  onSuccess?: () => void;
}

export function LinkFormDialog({ open, onOpenChange, link, onSuccess }: LinkFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const form = useForm<LinkFormData>({
    resolver: zodResolver(linkFormSchema),
    values: {
      name: link?.name || '',
      url: link?.url || '',
      description: link?.description || '',
      group_name: link?.group_name || '',
      icon_url: link?.icon_url || '',
      display_order: link?.display_order || 0,
      is_active: link?.is_active ?? true,
    },
  });

  const onSubmit = async (data: LinkFormData) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        url: data.url,
        description: data.description || null,
        group_name: data.group_name || null,
        icon_url: data.icon_url || null,
        display_order: data.display_order,
        is_active: data.is_active,
      };

      if (link) {
        const { error } = await api
          .from('links')
          .update(payload)
          .eq('id', link.id);

        if (error) throw error;

        toast({
          title: 'Enlace actualizado',
          description: 'El enlace se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('links')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Enlace creado',
          description: 'El enlace se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['links'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el enlace',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{link ? 'Editar Enlace' : 'Nuevo Enlace'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nombre del enlace" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL *</FormLabel>
                  <FormControl>
                    <Input {...field} type="url" placeholder="https://ejemplo.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción del enlace (opcional)" rows={3} />
                  </FormControl>
                  <FormDescription>
                    Breve descripción sobre el contenido del enlace
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="group_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grupo/Categoría</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Proveedores, Clientes..." />
                    </FormControl>
                    <FormDescription>
                      Agrupa enlaces por categoría
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="display_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden de visualización</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 0)}
                        min={0}
                        placeholder="0"
                      />
                    </FormControl>
                    <FormDescription>
                      Número para ordenar (menor = primero)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="icon_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL del icono</FormLabel>
                  <FormControl>
                    <Input {...field} type="url" placeholder="https://ejemplo.com/icon.png" />
                  </FormControl>
                  <FormDescription>
                    URL de la imagen del icono (opcional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El enlace es visible para todos los usuarios
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {link ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
